import { isSameDay, isThisYear } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type EventGroup<T> = {
  date: Date;
  events: T[];
  isTodayEmptyPlaceholder?: boolean;
  label: string;
};

type GroupedEvents<T extends { startTime: Date }> = {
  events: T[];
  shouldShowTodayEmptyPlaceholder?: boolean;
};

export const useGroupedEvents = <T extends { startTime: Date }>({
  events,
  shouldShowTodayEmptyPlaceholder,
}: GroupedEvents<T>) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const groupedEvents: Array<EventGroup<T>> = [];

    for (const event of events) {
      const eventStartTime = event.startTime;

      const group = groupedEvents.find((groupedEvent) => isSameDay(groupedEvent.date, eventStartTime));
      const label = isThisYear(eventStartTime)
        ? t('dateFormats.dayShortMonthDay', { date: eventStartTime })
        : t('dateFormats.dayShortMonthDayYear', { date: eventStartTime });

      if (group) {
        group.events.push(event);
      } else {
        groupedEvents.push({
          date: eventStartTime,
          events: [event],
          label,
        });
      }
    }

    if (shouldShowTodayEmptyPlaceholder) {
      groupedEvents.push({
        date: new Date(),
        events: [],
        isTodayEmptyPlaceholder: true,
        label: t('dateFormats.dayShortMonthDay', { date: new Date() }),
      });

      groupedEvents.sort((firstGroup, secondGroup) => firstGroup.date.getTime() - secondGroup.date.getTime());
    }

    return groupedEvents;
  }, [events, shouldShowTodayEmptyPlaceholder, t]);
};
